/**
 * Sentry browser SDK wrapper.
 *
 * - `initSentry()` is a no-op when VITE_SENTRY_DSN is unset. When set it **dynamically imports** the
 *   Sentry SDK (a separate async chunk). Because the `if (!DSN)` / `if (!DSN || …)` guards are
 *   build-time constants (DSN is `import.meta.env.VITE_SENTRY_DSN`), the dynamic imports are fully
 *   tree-shaken out of the build when no DSN is configured — so the SDK costs nothing unless enabled.
 * - `SentryErrorBoundary` is a plain React class boundary that reports caught errors via
 *   `captureError` (which lazily loads Sentry); no Sentry component is needed synchronously.
 * - `captureError` never throws.
 *
 * Privacy: query strings are stripped from breadcrumb + event request URLs (beforeBreadcrumb /
 * beforeSend), so a one-time token in the URL (e.g. /reset-password?token=…) never reaches Sentry.
 *
 * Sourcemaps are emitted via `build.sourcemap = true` in vite.config.ts.
 * Import from "../analytics" (the public API) rather than directly from this file.
 */
import type { ComponentType, ReactNode } from "react";
import React from "react";

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const TRACES_RATE = parseFloat(
  (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined) ?? "0.1",
);
const RELEASE = import.meta.env.VITE_APP_RELEASE as string | undefined;

let _sentryInited = false;

/** Drop the query string + hash so secrets (e.g. ?token=…) don't reach Sentry. */
function stripQuery(u: string): string {
  return u.replace(/[?#].*$/, "");
}

export function initSentry(): void {
  if (!DSN) return; // build-time constant ⇒ everything below is tree-shaken when telemetry is off
  void (async () => {
    try {
      const Sentry = await import("@sentry/react");
      Sentry.init({
        dsn: DSN,
        release: RELEASE,
        tracesSampleRate: Number.isNaN(TRACES_RATE) ? 0.1 : TRACES_RATE,
        integrations: [Sentry.browserTracingIntegration()],
        beforeBreadcrumb(crumb) {
          // Scrub query strings from navigation / xhr / fetch breadcrumb URLs.
          if (crumb.data) {
            for (const k of ["url", "from", "to"] as const) {
              const v = crumb.data[k];
              if (typeof v === "string") crumb.data[k] = stripQuery(v);
            }
          }
          if (typeof crumb.message === "string") crumb.message = stripQuery(crumb.message);
          return crumb;
        },
        beforeSend(event) {
          if (event.request?.url) event.request.url = stripQuery(event.request.url);
          return event;
        },
      });
      _sentryInited = true;
    } catch {
      // @sentry/react failed to load — no-op
    }
  })();
}

export function captureError(err: unknown, ctx?: Record<string, unknown>): void {
  if (!DSN || !_sentryInited) return; // DSN guard keeps the import tree-shaken when telemetry is off
  void (async () => {
    try {
      const Sentry = await import("@sentry/react");
      Sentry.withScope((scope) => {
        if (ctx) {
          Object.entries(ctx).forEach(([k, v]) => scope.setExtra(k, v));
        }
        Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
      });
    } catch {
      // never raise
    }
  })();
}

// ---------------------------------------------------------------------------
// SentryErrorBoundary — a plain React class boundary that reports caught errors via captureError
// (which lazily loads Sentry). No synchronous Sentry ErrorBoundary component is required, so nothing
// is bundled when DSN is off. The React component stack is forwarded for parity with Sentry's own.
// ---------------------------------------------------------------------------

interface ErrorBoundaryProps {
  fallback: ReactNode | ((props: { error: Error }) => ReactNode);
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class FallbackErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    captureError(error, { source: "ErrorBoundary", componentStack: info.componentStack });
  }

  render(): ReactNode {
    const { error } = this.state;
    if (error) {
      const { fallback } = this.props;
      return typeof fallback === "function" ? fallback({ error }) : fallback;
    }
    return this.props.children;
  }
}

export const SentryErrorBoundary: ComponentType<ErrorBoundaryProps> = FallbackErrorBoundary;
